<template>
  <div class='content'>
    <grid :name="$t('EvedPay Cards')"
          desc="Manage accounts, cards and transactions"
          search=true
          search-placeholder="My Cards"
          :sort="sort"
          resizing=false
          :endpoint="api.$.evedpaycard.cards"
          :views="true"
          alias="PaymentInvoiceList_SentInvoices"
          legacy
          :tabs="tabs"
          :row-classes="rowClasses"
          footer
          totals
          @selectedRows="rowSelected"
          ref="grid"
          :no-tooltip-fields="noTooltipFields"
          :xadditional-filters="additionalFilters"
          :filter="defaultFilter"
          can-remove-default-filter>
      <span slot="additionalButtons">
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('addFunds')"
                   v-tooltip="actionDisabledReason('addFunds')"
                   @success="refresh"
                   @click="() => {showAddFunds = true}">
          Add Funds
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('additionalUsers')"
                   v-tooltip="actionDisabledReason('additionalUsers')"
                   @success="refresh"
                   @click="() => {showCardAdditionalUsers = true}">
          Additional Users
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('returnFunds')"
                   v-tooltip="actionDisabledReason('returnFunds')"
                   @success="refresh"
                   @click="() => {showReturnFundsToAccount = true}">
          Return Funds to Account
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('refillSettings')"
                   v-tooltip="actionDisabledReason('refillSettings')"
                   @success="refresh"
                   @click="() => { isRefillOpen.showModal = true }">
          Refill Settings
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   :ids="selectedRows"
                   :disabled="loadingAccountSettings || cardActionDisabled('editAccountingSystemSettings')"
                   v-tooltip="loadingAccountSettings || actionDisabledReason('editAccountingSystemSettings')"
                   @success="refresh"
                   @click="() => { showAccountingSystemSettings = true }">
          PSL+ Settings
        </ed-button>       
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('holdActivity')"
                   v-tooltip="actionDisabledReason('holdActivity')"
                   @success="refresh"
                   @click="lockCard">
          {{ lockUnlockButtonText }}
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('reportLostStolen')"
                   v-tooltip="actionDisabledReason('reportLostStolen')"
                   @success="refresh"
                   @click="lostStolen">
          Report Lost or Stolen
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator') || hasAccountPermission('EvedPayCardViewReconcileClose')"
                   :id="selectedRows"
                   :disabled="cardActionDisabled('showCardDetails')"
                   v-tooltip="actionDisabledReason('showCardDetails')"
                   @success="refresh"
                   @click="() => {showCardDetails = true}">
          Card Details
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardAdministrator')"
                   :disabled="cardActionDisabled('requestPhysicalCard')"
                   v-tooltip="actionDisabledReason('requestPhysicalCard')"
                   @success="refresh"
                   @click="() => {showRequestPhysicalBatchOpen.showModal = true}">
          REQUEST PHYSICAL BATCH
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="hasAccountPermission('EvedPayCardViewReconcileClose')"
                   :ids="selectedRows"
                   :disabled="cardActionDisabled('close')"
                   v-tooltip="actionDisabledReason('close')"
                   @success="refresh"
                   @click="closeCard">
          Close Card
        </ed-button>
        <ed-button class="btn btn-actionbar"
                   v-if="showApproveButton && selectedRows.length"
                   :disabled="cardActionDisabled('approve')"
                   v-tooltip="actionDisabledReason('approve')"
                   @success="refresh"
                   @click="showApproveDialog = true">
          Approve
        </ed-button>
        <RejectCard v-if="showRejectButton && selectedRows.length"
                    :disabled="cardActionDisabled('reject')"
                    v-tooltip="actionDisabledReason('reject')"
                    :selected-rows="selectedRows"
                    :button-class="getButtonClass()"
                    @success="refresh" />
        <br />
        <!--<AddCard @success='refresh' :accountId="''" :divisionName="divisionName" :divisionsName="divisionsName" /> TODO:Finding a solution for later.-->
      </span>
      <span slot="additionalLabel" class="form-group no-margin">
        <i class="fas fa-exclamation"></i>
        <span>&nbsp;</span>
        <label>Click column headers to apply filters</label>
      </span>
    </grid>
    <ed-modal :modalEndpoint="`evedpaycards/accounts/${processedSelectedObj.AccountId}/get-address`" title="Card Details" :show="showCardDetails" @close="showCardDetails = false" primary-button="Close" secondary-button="false">
      <CardDetails slot="body" :card="processedSelectedObj" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/request-physical-card`"
              title="Request Physical Card Batch"
              :show="showRequestPhysicalBatchOpen.showModal"
              @close="showRequestPhysicalBatchOpen.showModal = false"
              primary-button="Request"
              @validate="$refs.physicalCardComponent.requestPhysicalCard(showRequestPhysicalBatchOpen)"
              validation>
      <RequestPhysicalBatch v-if="selectedRows.length"
                            ref="physicalCardComponent"
                            :list="selectedRows"
                            :divisionId ="selectedRows[0].data.DivisionId"
                            @success="refresh"
                            slot="body" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/${this.cardStatus}`" :title="modalTitle" :show="showCardStatus.showModal" @close="showCardStatus.showModal = false" @validate="$refs.cardStatusComponent.changeStatus(cardStatus, showCardStatus)" primary-button="Submit" type="medium" validation>
      <CardStatus ref="cardStatusComponent" slot="body" :status="cardStatus" :cards="selectedObj" />
    </ed-modal>
    <ed-modal title="Approval Needed" :show="showApprovalUsers" @close="showApprovalUsers = false" primary-button="Close" secondary-button="false">
      <ApprovalNeededUsers slot="body" :approvalUsersData="approvalUsersData" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/add-funds`" title="Add Funds" :show="showAddFunds" @close="showAddFunds = false" validation @validate="addFunds"
              primary-button="Add" @confirm="addfunds()">
      <CardAddFunds slot="body" :addFundsError="this.addFundsErrors" :balance="accountBalance" @fundingAmount="(value) => fundingAmount = value" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/additional-users`" title="Additional Users" :show="showCardAdditionalUsers" @close="showCardAdditionalUsers = false" @confirm="updateAdditionalUsers" primary-button="Save">
      <CardAdditionalUsers slot="body" :divisionId="processedSelectedObj.DivisionId" :cardId="processedSelectedObj.Id" :cardHolderId="processedSelectedObj.HolderUserId" @additionalUsers="(o) => additionalUsers = o" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/remove-funds`" title="Return Funds to Account" :show="showReturnFundsToAccount" @close="showReturnFundsToAccount = false" primary-button="Submit" validation @validate="doReturnFunds" @confirm="doReturnFunds()">
      <ReturnFunds :card="processedSelectedObj" slot="body" @returnFunds="(value) => returnFunds = value" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/refill-settings`" title="Refill Settings" :show="isRefillOpen.showModal" @close="isRefillOpen.showModal = false" primary-button="Save" @validate="$refs.cardRefillComponent.doRefill(isRefillOpen)" validation>
      <RefillSettings ref="cardRefillComponent" :card="processedSelectedObj" slot="body" @refillSuccess="showRefillToast" />
    </ed-modal>
    <ed-modal :modalEndpoint="`evedpaycards/${processedSelectedObj.Id}/accounting-system-settings`"
      :title="'PSL+ Settings'" primary-button="Save" :show="showAccountingSystemSettings" 
      @validate="handlePslSettingsSave"  @close="showAccountingSystemSettings = false" validation>
      <AccountingSystemSettings ref="accountingSystemSettingsCmp" :card="processedSelectedObj" :accountSettings="accountSettings[objDivisionId]" slot="body" />
    </ed-modal>
    <ApproveDialog :showModal="showApproveDialog"
                   :list="transformRows(selectedRows)"
                   :programLabel="programLabel"
                   cardApproval
                   @approve="doApprove"
                   @close="() => { showApproveDialog = false }" />
  </div>
</template>

<script>

  import DataSkybox from '@/components/_modules/Invoices/data-skybox'

  import Grid from '@/components/Grid'
  import { EventBus } from '@/event-bus'
  import model from './model.js'
  import tabs from '@/components/_modules/Programs/Card-accounts/tabs'
  import CardDetails from '@/components/_modules/Programs/Card-list/CardAddress.vue'
  import CardStatus from '@/components/_modules/Programs/Card-list/CardStatus.vue'
  import ApproveDialog from '@/components/_modules/Dashboard/widgets/ApprovalNeeded/approve-dialog'
  import RejectCard from '@/components/_modules/Programs/Card-list/RejectCard.vue'
  import ApprovalNeededUsers from '@/components/_modules/Programs/Card-list/ApprovalNeededUsers.vue'
  import AddCard from '@/components/_modules/Programs/Card-list/AddCard.vue'
  import RequestPhysicalCard from '@/components/_modules/Programs/Card-list/RequestPhysicalCard.vue'
  import RequestPhysicalBatch from '@/components/_modules/Programs/Card-list/RequestPhysicalBatch.vue'
  import CardAddFunds from '@/components/_modules/Programs/Card-list/CardAddFunds.vue'
  import ReturnFunds from '@/components/_modules/Programs/Card-list/ReturnFundsToAccount.vue'
  import RefillSettings from '@/components/_modules/Programs/Card-list/CardRefill.vue'
  import AccountingSystemSettings from '@/components/_modules/Programs/Card-list/AccountingSystemSettings.vue'
  import CardAdditionalUsers from '@/components/_modules/Programs/Card-list/CardAdditionalUsers.vue'

  // import users from '@/core/legacy-auth/users'

  import CancelInvoice from '@/components/_modules/Invoices/actions/cancel'
  import EditInvoiceForSupplier from '@/components/_modules/Invoices/actions/edit-invoice'

  import config, { metaStatuses, cannotCancelReasons } from '@/components/_modules/Invoices/statuses'
  const { additionalFilters, filters, isActiveFilter } = config

  const defaultFilter = []

  const cancelTooltip = 'Please select a card.'

  export default {
    components: {
      DataSkybox,
      Grid,
      CancelInvoice,
      EditInvoiceForSupplier,
      CardDetails,
      CardStatus,
      ApprovalNeededUsers,
      AddCard,
      RequestPhysicalCard,
      RequestPhysicalBatch,
      ApproveDialog,
      RejectCard,
      CardAddFunds,
      ReturnFunds,
      RefillSettings,
      AccountingSystemSettings,
      CardAdditionalUsers
    },

    data() {
      return {
        additionalFilters,
        defaultFilter: this.$route.query.filter
          ? filters(this.$route.query.filter.split(','))
          : filters(defaultFilter),
        statuses: metaStatuses,
        model,
        tabs: tabs.tabs,
        sort: { CardNumber: 'desc' },
        rowClasses: {
          'negative-amount': params => (params.data || {}).TotalAmount < 0
        },
        selectedRows: [],
        selectedObj: [],
        selectedAll: null,
        asyncCancelInvoiceDisabledReason: cancelTooltip,
        hasData: false,
        cardsList: [{}],
        showCardDetails: false,
        showCardStatus: { showModal: false },
        showAddCard: false,
        showRequestPhysicalCardOpen: { showModal: false },
        showRequestPhysicalBatchOpen: { showModal: false },
        mailingAddress: {},
        cardStatus: '',
        divisionName: '',
        divisionsName: '',
        objDivisionId: null,
        modalTitle: '',
        showApprovalUsers: false,
        approvalUsersData: {},
        showApproveDialog: false,
        noTooltipFields: ['CardStatus'],
        addFundsErrors: false,
        showAddFunds: false,
        fundingAmount: 0,
        showReturnFundsToAccount: false,
        returnFunds: '',
        isRefillOpen: { showModal: false },
        accountSettings: [],
        loadingAccountSettings: false,
        showAccountingSystemSettings: false,
        showCardAdditionalUsers: false,
        additionalUsers: []
      }
    },
    watch: {
      '$route.query.filter'(value, oldValue) {
        if (value && value !== oldValue) {
          this.$refs.grid.gridOptions.api.setFilterModel(null)
          this.defaultFilter = filters(value.split(','), {}, this.$route.query.filter && this.$route.query.filter.split(','))
          this.$refs.grid.gridOptions.api.setFilterModel(this.defaultFilter)
        }
      }
    },
    computed: {
      accountBalance() {
        if (this.selectedObj.length && this.selectedObj[0]) {
          return this.selectedObj[0].AccountBalance
        }
        return {}
      },
      processedSelectedObj() {
        return this.selectedObj[0] || {}
      },
      selectedId() {
        return this.selectedRows && this.selectedRows[0] && this.selectedRows[0].data &&
          this.selectedRows[0].data.PaymentInvoiceID
      },
      lockUnlockButtonText() {
        if (this.selectedObj.length > 0) {
          const cardStatus = this.selectedObj[0].CardStatus
          switch (cardStatus) {
            case 'Active':
              return 'Lock Card'
            case 'Locked':
              return 'Unlock Card'
            case 'Locked - Lost/Stolen':
              return 'Unlock Card'
            case 'Closed':
              return 'Lock Card'
            default:
              return 'Lock Card'
          }
        }
        return 'Lock Card'
      },
      showApproveButton() {
        return this.isUserInApprovalList()
      },
      showRejectButton() {
        return this.isUserInApprovalList()
      }
    },
    methods: {
      async addFunds() {
        if (this.fundingAmount <= 0 || this.fundingAmount > this.selectedObj[0].AccountBalance) {
          this.addFundsErrors = true
          this.$emit('addFundsError', this.addFundsErrors)
          return
        }
        try {
          const response = await this.api.post(`evedpaycards/${this.selectedObj[0].Id}/add-funds`, {
            Amount: parseFloat(this.fundingAmount)
          })

          if (response.status === 200) {
            this.$emit('success')
            this.addFundsErrors = false
            this.showAddFunds = false
            this.refresh()
          }
        } catch (error) {
          console.error(error)
        }
        this.fundingAmount = ''
        this.addFundsErrors = false
      },

      async doReturnFunds() {
        if (!(parseFloat(this.returnFunds) > 0 && parseFloat(this.returnFunds) <= parseFloat(this.selectedObj[0].Balance))) {
          alert('Input cannot be 0, negative, or more than the current balance.')
          return
        }
        try {
          const response = await this.api.post(`evedpaycards/${this.selectedObj[0].Id}/remove-funds`, {
            Amount: parseFloat(this.returnFunds)
          })

          if (response.status === 200) {
            this.showReturnFundsToAccount = false
            this.refresh()
          } else {
            this.showReturnFundsToAccount = false
            alert('Failed to return funds.')
          }
        } catch (error) {
          console.error(error)
        }
        this.returnFunds = ''
      },

      async updateAdditionalUsers() {
        try {
          const response = await this.api.patch(`evedpaycards/${this.selectedObj[0].Id}/additional-users`, {
            users: this.additionalUsers
          })
          if (response.status === 200) {
            this.refresh()
            console.log(response.statusText)
          }
        } catch (error) {
          console.error(error)
        }
      },

      async approveOrReject(cardId) {
        try {
          let payload = {
            cardsData: JSON.stringify([{ Id: cardId }])
          }
          const response = await this.api.post(this.api.$.evedpaycard.approveOrReject, payload)
          if (!response.data) {
            throw new Error('Error during the request')
          }

          this.showApproveDialog = false
          this.refresh()
        } catch (error) {
          console.error(error)
        }
      },

      doApprove() {
        this.approveOrReject(this.selectedRows[0].data.Id)
      },

      transformRows(rows) {
        return rows.map(r => {
          let amount = parseFloat(r.data.InitialLoad).toFixed(2)
          let row = {
            NeedsApproval: r.data.CardStatus === 'Pending Approval' ? 'EvedPay Card' : '',
            Amount: amount + ' USD'
          }
          return row
        })
      },

      async handlePslSettingsSave() {
        const res = await this.$refs.accountingSystemSettingsCmp.save()
        this.showAccountingSystemSettings = !res
        return res
      },

      getApprovalUsers(cardId) {
        this.api.get(this.api.$.evedpaycard.approvalUsers, {
          params: {
            cardId
          }
        })
          .then(response => {
            this.approvalUsersData = response.data
          })
          .catch(error => {
            console.error('There was a problem with the approval users request:', error)
          })
      },

      isUserInApprovalList() {
        const currentUserID = this.$root.getUser.$.user.UserID
        if (Array.isArray(this.approvalUsersData)) {
          return this.approvalUsersData.some(user => user.Id === currentUserID && user.IsCurrentAction)
        }
        return false
      },

      async getAccountSettings(divisionId) {
        if (this.accountSettings[divisionId]) {
          return
        }

        this.loadingAccountSettings = true
        try {
          const response = await this.api.get(`evedpaycards/accounts/${divisionId}/settings-by-division`)
          if (response.status === 200) {
            this.accountSettings[divisionId] = response.data
          }
        } catch (error) {
          console.log(error)
        } finally {
          this.loadingAccountSettings = false
        }
      },

      async handleButtonClick(cardId) {
        try {
          await this.getApprovalUsers(cardId)

          this.showApprovalUsers = true
        } catch (error) {
          console.error('There was a problem handling the button click:', error)
        }
      },

      getButtonClass() {
        if (this.selectedRows.length >= 0) {
          return 'btn btn-actionbar'
        } else {
          return 'btn btn-actionbar col-md-12'
        }
      },

      hasAccountPermission(permission) {
        if (this.$user && this.$user.$.userPermissions && this.$user.$.userPermissions.length !== 0) {
          return this.$user.$.userPermissions.includes(permission)
        }

        return false
      },

      cardActionDisabled(actionName) {
        if (actionName !== 'close' && actionName !== 'requestPhysicalCard' && this.selectedRows.length > 1) {
          return true
        }
        return !!this.actionDisabledReason(actionName)
      },

      actionDisabledReason(actionName) {
        const userId = this.$user.$.user.UserID

        if (this.selectedRows.length > 1 && actionName !== 'close' && actionName !== 'requestPhysicalCard') {
          switch (actionName) {
            case 'edit':
              return 'You can only edit one card at a time.'
            case 'holdActivity':
              return 'You can only lock/unlock a single card at a time.'
            case 'showCardDetails':
              return 'You can only show the card details for one card at a time.'
            case 'addFunds':
              return 'You can only add funds for one card at a time.'
            case 'returnFunds':
              return 'You can only return funds for one card at a time.'
            case 'reportLostStolen':
              return 'You can only report a single card as being lost/stolen at a time.'
            case 'additionalUsers':
              return 'You can only add additional users for a single card at a time.'
            case 'refillSettings':
              return 'You can only update the refill settings for one card at a time.'
            case 'editAccountingSystemSettings':
              return 'You can only update the card settings for one card at a time.'
            default:
              return cancelTooltip
          }
        }
        if ((this.selectedObj.some(card => card.CardStatus !== 'Pending Approval')) && (actionName === 'approve' || actionName === 'reject') && actionName !== 'requestPhysicalCard') {
          return 'You can select cards that are waiting for the approval process.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Locked') && (actionName !== 'holdActivity' && actionName !== 'showCardDetails' && actionName !== 'requestPhysicalCard')) {
          return 'This card is locked.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Closed') && actionName !== 'showCardDetails' && actionName !== 'requestPhysicalCard') {
          return 'One or more cards are already closed.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Locked - Lost/Stolen') && actionName !== 'holdActivity' && actionName !== 'requestPhysicalCard') {
          return 'This card is Lost/Stolen. Contact Help Desk for further assistance.'
        }
        if (this.selectedObj.some(card => card.CardStatus !== 'Active' || card.IsPhysicalCard === true) && actionName === 'requestPhysicalCard') {
          return 'Only Active cards that have not had a physical card ordered can be requested.'
        }
        if (this.selectedObj.some(card => card.HolderUserId !== userId) && actionName === 'showCardDetails') {
          return 'You are not the card holder.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Processing')) {
          return 'Please wait for the card to be processed!'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Inactive')) {
          return 'This card is Inactive, please select another.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Pending Approval') && (actionName !== 'approve' || actionName !== 'reject')) {
          return 'This card is Pending Approval, please select another.'
        }
        if (this.selectedObj.some(card => card.CardStatus === 'Rejected')) {
          return 'This card is declined.'
        }
        if (actionName === 'editAccountingSystemSettings' && this.selectedRows.length === 1) {
          let accountSettings = Array.isArray(this.accountSettings) ? this.accountSettings[this.selectedObj[0].DivisionId] : this.accountSettings
          if (!accountSettings || !accountSettings.AccountingSystem || accountSettings.AccountingSystem.SyncEvedPayCards !== true) {
            return '"PSL+ Integration - Sync Card data" is unchecked.'
          }
        }
        if (this.selectedRows.length === 0) {
          return cancelTooltip
        }
      },

      async rowSelected(event) {
        if (typeof event === 'boolean') {
          this.selectedAll = event
        } else {
          this.selectedRows = event
          this.fetchCancelReason()
          this.getApprovalUsers(this.selectedRows[0].data.Id)
          this.isUserInApprovalList()
        }
        if (this.selectedRows.length > 0) {
          this.selectedObj = this.selectedRows.map(card => card.data)
          this.objDivisionId = this.selectedObj[0].DivisionId
          await this.getAccountSettings(this.objDivisionId)
        } else {
          this.selectedObj = []
        }
      },

      refresh() {
        this.$refs.grid.refreshGrid()
      },

      handleStatusFilter(status) {
        this.$router.push({
          query: {
            filter: undefined
          }
        })

        this.$refs.grid.gridOptions.api.setFilterModel(
          filters(
            [status],
            this.$route.query.filter
              ? {}
              : this.$refs.grid.gridOptions.api.getFilterModel()
          )
        )
      },

      statusActive(status) {
        if (!this.$refs.grid) return {}
        return isActiveFilter(
          status,
          this.$refs.grid.gridOptions.api.getFilterModel(),
          this.$route.query.filter && this.$route.query.filter.split(',')
        )
      },

      async fetchCancelReason() {
        if (!this.selectedRows.length || this.selectedRows.length > 1) {
          this.asyncCancelInvoiceDisabledReason = cancelTooltip
          return
        }

        const tooltip = await this.api.forms.post(this.api.$.invoice.list.canCancelInvoice,
          {
            id: this.selectedId
          })
        const reason = cannotCancelReasons[tooltip.data.SuccessMessage]
        this.asyncCancelInvoiceDisabledReason = reason || ''

        return reason
      },

      singularize(value) {
        return value.endsWith('s') ? value.slice(0, -1) : value
      },

      lockCard() {
        if (this.selectedObj.length > 0) {
          const selectedCard = this.selectedObj[0]
          this.modalTitle = selectedCard.CardStatus === 'Active' ? 'Lock Card Activity' : 'Unlock Card Activity'
          this.cardStatus = selectedCard.CardStatus === 'Active' ? 'lock' : 'unlock'
          this.showCardStatus.showModal = true

          this.$nextTick(() => {
            this.$refs.cardStatusComponent.isActive = selectedCard.CardStatus === 'Locked'
          })
        }
      },

      lostStolen() {
        this.modalTitle = 'Report Card Lost or Stolen'
        this.showCardStatus.showModal = true
        this.cardStatus = 'report-lost-or-stolen'
      },

      closeCard() {
        this.modalTitle = 'Close EvedPay Card'
        this.showCardStatus.showModal = true
        this.cardStatus = 'close'
      }
    },

    created() {
      this.divisionName = this.singularize(this.$root.getUser.settings.Labels.DivisionName)
      this.divisionsName = this.$root.getUser.settings.Labels.DivisionsName
      this.model.Division.label = this.divisionName
      EventBus.$off('status-changed')
      EventBus.$on('status-changed', this.refresh)
      EventBus.$off('physicalCardRequested')
      EventBus.$on('physicalCardRequested', this.refresh)
      EventBus.$off('refill')
      EventBus.$on('refill', this.refresh)
      this.hasAccountPermission()
    }
  }

</script>

<style lang="scss" scoped>
  @import "~@/assets/sass/_variables.scss";

  .grid >>> {
    .ag-row.negative-amount {
      @include theme {
        background-color: lighten(v(urgent-02), 45%);
      }
    }
    .action-buttons .col-md-6 {
      width: 100%;
    }
  }

  .statuses-bar {
    line-height: 40px;
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-cards/index.vue